/**
 * NewTicketPage — form to file a fresh support ticket.
 */
import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { DashboardHeader } from '@/src/modules/components/Header'
import { Icons } from '@/src/modules/components/Icons'
import { cn } from '@/src/infrastructure/lib/utils'
import {
  createSupportTicket,
  type SupportTicketCategory,
  type SupportTicketPriority,
} from '@/src/infrastructure/lib/api'
import { SUPPORT_TICKET_CATEGORY_OPTIONS } from './constants'

const SUBJECT_MIN = 5
const SUBJECT_MAX = 160
const DESC_MIN = 10
const DESC_MAX = 5000

export function NewTicketPage() {
  const navigate = useNavigate()
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<SupportTicketCategory>('ACCOUNT')
  const [priority, setPriority] = useState<SupportTicketPriority>('MEDIUM')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const subjectValid = subject.trim().length >= SUBJECT_MIN && subject.trim().length <= SUBJECT_MAX
  const descValid = description.trim().length >= DESC_MIN && description.trim().length <= DESC_MAX
  const canSubmit = subjectValid && descValid && !submitting

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError('')
    try {
      const ticket = await createSupportTicket({
        subject: subject.trim(),
        category,
        priority,
        description: description.trim(),
        metadata: {
          platform: 'WEB',
          browser: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 120) : undefined,
        },
      })
      navigate(`/dashboard/help/tickets/${ticket.id}`, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create ticket')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="px-4 md:px-8 py-6 md:py-10 space-y-6 max-w-2xl mx-auto">
      <Link
        to="/dashboard/help"
        className="inline-flex items-center gap-1 text-xs font-semibold text-on-surface-variant hover:text-on-surface"
      >
        <Icons.ChevronLeft size={14} /> Back to help center
      </Link>

      <DashboardHeader
        title="Contact support"
        description="Tell us what's going on. We typically reply within a business day."
      />

      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-outline-variant/40 bg-surface-low/40 p-6 md:p-8 space-y-5"
      >
        <div>
          <label className="block text-xs font-semibold text-on-surface">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={SUBJECT_MAX}
            className="mt-1 w-full rounded-xl border border-outline-variant/40 bg-surface px-3 py-2 text-sm focus:border-primary focus:outline-none"
            placeholder="Cannot publish my wedding invitation"
          />
          <p className="mt-1 text-[10px] text-on-surface-variant">
            {subject.trim().length} / {SUBJECT_MAX} characters
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold text-on-surface mb-1">Category</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {SUPPORT_TICKET_CATEGORY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setCategory(opt.value)}
                aria-pressed={category === opt.value}
                className={cn(
                  'rounded-xl border px-3 py-2 text-left text-xs transition-colors',
                  category === opt.value
                    ? 'border-primary bg-primary/10 text-on-surface'
                    : 'border-outline-variant/40 text-on-surface-variant hover:border-primary/40',
                )}
              >
                <span className="block font-semibold text-on-surface">{opt.label}</span>
                <span className="block text-[10px] text-on-surface-variant mt-0.5">{opt.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-on-surface mb-1">Priority</label>
          <div className="flex gap-2">
            {(['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as SupportTicketPriority[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPriority(p)}
                aria-pressed={priority === p}
                className={cn(
                  'flex-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                  priority === p
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-outline-variant/40 text-on-surface-variant',
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-on-surface">
            How can we help?
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={DESC_MAX}
            rows={8}
            className="mt-1 w-full rounded-xl border border-outline-variant/40 bg-surface px-3 py-2 text-sm focus:border-primary focus:outline-none resize-y"
            placeholder="Describe the problem, including steps to reproduce if it's a bug."
          />
          <p className="mt-1 text-[10px] text-on-surface-variant">
            {description.trim().length} / {DESC_MAX} characters
          </p>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between">
          <p className="text-[11px] text-on-surface-variant">
            We auto-attach your browser version + plan for context.
          </p>
          <button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              'inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-semibold shadow-sm transition-colors',
              canSubmit
                ? 'bg-primary text-white hover:bg-brand-deep'
                : 'bg-surface-low text-on-surface-variant cursor-not-allowed',
            )}
          >
            {submitting ? 'Submitting…' : 'Send ticket'}
          </button>
        </div>
      </motion.form>
    </div>
  )
}
